import { useState, useEffect, useCallback } from 'react';
import { GetShipmentAlerts } from '../apis/shipment';
import useUnauthorize from './useUnauthorize';
import { useToken } from '../common/helper';
import { isZoomlogiSupport } from '@/common/utils/utils';
import { ShipmentAlertData } from '@/apis/types';

interface UseFetchShipmentAlertsReturn {
  alerts: ShipmentAlertData[];
  refetchAlerts: () => void;
}

/**
 * Hook to fetch and manage shipment alerts
 *
 * @param shipmentId - The ID of the shipment to fetch alerts for. If null/undefined, returns empty alerts
 * @param companyName - The name of the company. Fetching is skipped for "Zoomlogi Support" company
 */
const useFetchShipmentAlerts = (
  shipmentId?: string,
  companyName?: string
): UseFetchShipmentAlertsReturn => {
  const { checkTokenExpiry } = useToken();
  const { unauthorize } = useUnauthorize();
  const [alerts, setAlerts] = useState<ShipmentAlertData[]>([]);

  const fetchShipmentAlerts = useCallback(() => {
    if (!shipmentId) {
      setAlerts([]);
      return;
    }
    const token = checkTokenExpiry();
    if (!token) {
      return;
    }
    GetShipmentAlerts(token, shipmentId)
      .then((res) => {
        const responseData = res?.data;
        if (
          responseData?.data?.is_success &&
          Array.isArray(responseData?.data?.data)
        ) {
          setAlerts(responseData.data.data);
        } else {
          setAlerts([]);
        }
      })
      .catch((err) => {
        unauthorize(err);
        setAlerts([]);
      });
  }, [checkTokenExpiry, shipmentId, unauthorize]);

  useEffect(() => {
    if (!isZoomlogiSupport(companyName)) {
      fetchShipmentAlerts();
    }
  }, [fetchShipmentAlerts, companyName]);

  return { alerts, refetchAlerts: fetchShipmentAlerts };
};

export default useFetchShipmentAlerts;
